import React, { memo, useMemo, useCallback } from 'react';
import { classnames } from '@core/utils/css';
import { useProposalContext } from '../../ProposalContext';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { Proposal } from '@modules/proposal/Blocks/types';
import { SortableSectionItem } from './SectionItem';
import { SortableList } from '@modules/common/Sortable';
import './index.scss';
import { SortEnd } from 'react-sortable-hoc';
import ScrollBar from 'react-perfect-scrollbar';
import { ProposalBlocsHandle } from '../ProposalPage';
import { buildNavEntries, builderGroupRows, countTopLevelSections, MAX_TOP_LEVEL_SECTIONS, navLabel, resolveParents } from './navGroups';
import { moveBlock } from './blockOrder';
interface ProposalSectionsProps {
  moveTo(index: number): void;
  handleRef: React.RefObject<ProposalBlocsHandle>;
}
export const ProposalSections = memo((props: ProposalSectionsProps) => {
  const { moveTo, handleRef } = props;
  const { sidebar, form } = useProposalContext()!;
  const formValue = useNonNilObservable(form.value$);

  const onSortEnd = useCallback(
    (sort: SortEnd) => {
      const oldIndex = sort.oldIndex;
      const newIndex = sort.newIndex;
      if (oldIndex === newIndex) {
        return;
      }

      // Dragging a section moves it with its sub-sections and snaps to a section boundary; dragging
      // a plain block moves just that block. (The old menuOrder arithmetic inserted at an index that
      // ignored the removal shift, so dropping a section next to another one absorbed its blocks.)
      const result = moveBlock(formValue.blocks, oldIndex, newIndex);
      if (result.blocks !== formValue.blocks) {
        form.patchValue({ blocks: result.blocks });
      }
      moveTo(result.index);
    },
    [formValue.blocks, form, moveTo],
  );

  // How many top-level entries the client navigation will show (groups + ungrouped sections).
  const topLevelCount = useMemo(() => countTopLevelSections(buildNavEntries(formValue.blocks)), [formValue.blocks]);
  // Group headings for the sidebar, one row per block (keeps rows aligned with block indexes).
  const groupRows = useMemo(() => builderGroupRows(formValue.blocks), [formValue.blocks]);
  // Parent sections: rows stay in page order (drag-sort maps rows 1:1 to blocks), so a nested section is indented
  // and names its parent instead of being moved under it.
  const { parentOf, depthOf } = useMemo(() => resolveParents(formValue.blocks), [formValue.blocks]);

  return (
    <div className={classnames('proposal-sections', { open: sidebar })}>
      <div
        className={classnames('px-3 pb-2 font-size-12', topLevelCount > MAX_TOP_LEVEL_SECTIONS ? 'text-danger' : 'text-muted')}
        title="Parent sections and blocks sharing a Navigation Group count as one top-level section each; nested sections don't count"
      >
        Top-level sections: {topLevelCount} / {MAX_TOP_LEVEL_SECTIONS}
      </div>
      <ScrollBar style={{ height: '100%', overflow: 'hidden' }}>
        <SortableList lockAxis="y" distance={2} onSortEnd={sort => onSortEnd(sort)} useDragHandle={true} helperClass="z5000">
          {formValue.blocks.map((bloc, index) => {
            return (
              <SortableSectionItem
                index={index}
                key={index}
                block={bloc}
                blockForm={form.controls.blocks.items[index]}
                groupLabel={groupRows[index]?.label}
                grouped={groupRows[index]?.grouped}
                isParent={!!bloc.isParentSection}
                parentName={parentOf.has(index) ? navLabel(formValue.blocks[parentOf.get(index)!]) : undefined}
                depth={depthOf(index)}
                onClick={() => {
                  // Open any collapsed parents on the page first, then scroll
                  handleRef.current?.reveal(index);
                  moveTo(bloc.menuOrder);
                }}
                onSettings={() => handleRef.current?.openSettings(index)}
              />
            );
          })}
        </SortableList>
      </ScrollBar>
    </div>
  );
});
